// localStorage offline-backup layer.
// 2026-05-29 EXTRACTED from src/App.jsx (C1 Phase 2e shared-helpers).
// Priority (in App.jsx data layer): API (shared between all users) > localStorage.
// LS_KEYS + lsGet are App.jsx-internal only (no external importers); lsSave is
// re-exported from App.jsx for backward compat (many page importers + atomic
// libs receive it via DI param). Bodies reference only LS_KEYS + global localStorage.

const LS_KEYS = { users: 'blitz_users', payments: 'blitz_payments', 'customer-payments': 'blitz_cp', 'crg-deals': 'blitz_crg', 'daily-cap': 'blitz_dc', 'deals': 'blitz_deals', 'wallets': 'blitz_wallets', 'offers': 'blitz_offers', 'partners': 'blitz_partners', 'conference-folders': 'blitz_conf_folders', 'conference-partners': 'blitz_conf_partners', 'ftd-entries': 'blitz_ftd', 'daily-calcs-data': 'blitz_dailycalcs' };

// 2026-05-29 EXTRACTED from src/App.jsx (C1 Phase 2d3b2). localStorage key for
// persisted data-version map. Used by App.jsx (version-upgrade cleanup +
// load/savePersistedVersions) and by RestoreDatabase (clears it on restore).
export const LS_VERSIONS_KEY = 'blitz_data_versions';

// 2026-06-04 (audit F2): localStorage keys for the DURABLE delete/tombstone trackers
// (src/lib/tombstones.js). persistDeletedIDs + tombstonedIDs are mirrored here so a
// user's deletes survive a page reload — without this, merge-by-id.js resurrects a
// deleted (money) row from server data after the in-memory sets reset on reload.
// Cleared alongside LS_VERSIONS_KEY by RestoreDatabase (a restore replaces ALL data —
// a stale tombstone must not hide a record present in the restored backup). Deliberately
// NOT cleared on version-upgrade: that IS the reload these trackers must survive.
export const LS_DELETED_IDS_KEY = 'blitz_deleted_ids';
export const LS_TOMBSTONED_IDS_KEY = 'blitz_tombstoned_ids';

// 2026-06-09 (v32.16.52 hotfix) — DERIVED per-endpoint key for endpoints absent from
// LS_KEYS. The 11 SIMPLE_ENDPOINTS_NO_GUARD (gen-balances, today-transactions, tx-archive,
// invoices, invoice-settings, blitz-reports*, selling-offers, selling-settings, ds-archive,
// daily-summary) had LS_KEYS[key] === undefined → localStorage.getItem/setItem(undefined)
// coerces to the SHARED string key "undefined", colliding ALL of them onto ONE slot.
// v32.16.45 made apiSave's deferred-retry re-read via lsGet(endpoint), so a retry for one
// endpoint pulled ANOTHER's data out of that shared slot → cross-endpoint corruption
// (gen-balances overwritten by today-transactions, prod 2026-06-09 12:06 UTC).
// Fix: derive a UNIQUE key per endpoint ('blitz_<endpoint>') when absent from LS_KEYS.
// Every endpoint now owns its own slot → the v32.16.45 re-read sends ITS OWN latest
// (benefit preserved) AND no endpoint can read/write another's data (collision gone).
// Registered tables keep their existing keys (no behavior change, backward-compatible).
// See session_2026_06_09_lskeys_undefined_collision.md.
const lsKeyFor = (key) => LS_KEYS[key] || ('blitz_' + key);

function readStoredValue(key, fallback) {
  try {
    const raw = localStorage.getItem(lsKeyFor(key));
    return { ok: true, value: raw ? JSON.parse(raw) : fallback };
  } catch {
    return { ok: false, value: fallback };
  }
}

function lsGet(key, fallback) {
  return readStoredValue(key, fallback).value;
}

// 2026-07-28 (аудит прав): убрать таблицу из локального кэша браузера.
// Нужен ровно для случая «сервер ответил 403 на чтение»: если у сотрудника отобрали
// доступ к таблице, её строки не должны продолжать лежать у него в localStorage —
// иначе гейт на бэкенде закрыт, а данные всё равно остаются на его машине.
// Не бросает: приватный режим Safari / заблокированное хранилище кидают на любой доступ.
export function lsRemove(key) {
  try {
    localStorage.removeItem(lsKeyFor(key));
    return true;
  } catch {
    return false;
  }
}

export function lsSave(key, data) {
  const lsKey = lsKeyFor(key);
  try {
    localStorage.setItem(lsKey, JSON.stringify(data));
    return true;
  } catch(e) {
    // FIX C8: localStorage full — warn user, don't silently lose data
    if (e.name === 'QuotaExceededError' || e.code === 22) {
      console.error("❌ localStorage FULL — data NOT cached locally for:", key);
      // One non-destructive retry: never evict another table to persist this one.
      try {
        localStorage.setItem(lsKey, JSON.stringify(data));
        return true;
      } catch {
        return false;
      }
    }
    return false;
  }
}

export function lsReconcileRecord(key, id, record, { expectedRequestId } = {}) {
  if (!id || !record || typeof record !== 'object' || !expectedRequestId) {
    return { status: 'unavailable', reason: 'invalid_input' };
  }
  const stored = readStoredValue(key, []);
  if (!stored.ok) return { status: 'unavailable', reason: 'read_failed' };
  const current = stored.value;
  if (!Array.isArray(current)) {
    return { status: 'unavailable', reason: 'invalid_snapshot' };
  }
  const index = current.findIndex((row) => row && row.id === id);
  if (index < 0) return { status: 'absent' };
  const existing = current[index];
  if (existing._pendingRequestId !== expectedRequestId) {
    return { status: 'suppressed', reason: 'request_mismatch' };
  }
  const existingVersion = existing.version;
  const nextVersion = record.version;
  if (
    Number.isSafeInteger(existingVersion) &&
    existingVersion >= 1 &&
    (!Number.isSafeInteger(nextVersion) || nextVersion < existingVersion)
  ) {
    return { status: 'suppressed', reason: 'newer_version' };
  }
  const next = [...current];
  next[index] = record;
  return lsSave(key, next)
    ? { status: 'applied' }
    : { status: 'write_failed' };
}

export { LS_KEYS, lsGet };
